import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { AxiosResponse } from 'axios';
import { io, Socket } from 'socket.io-client';
import { apiClient } from '../api';
import { AuthContext } from './AuthContext';
import { getConfigVar } from '../config';

import { ChatMessage, ChatRoom, Device, Loadable } from '../types';

interface ChatContextType {
  device?: Device;
  messages: Loadable<ChatMessage>;
  chatRooms?: Loadable<ChatRoom>;
  currentRoom?: ChatRoom | undefined;
  effectiveRoom?: ChatRoom | undefined; // Added effectiveRoom to expose the effective room (current or lobby)
  sendMessage: (text: string) => Promise<void>;
  joinRoom: (room: ChatRoom | undefined) => void;
  createRoom: (name: string, type?: string, members?: string[]) => Promise<ChatRoom>;
  updateRoom: (room: ChatRoom, name: string, members?: string[]) => Promise<ChatRoom>;
  deleteRoom: (room: ChatRoom) => Promise<void>;
  reloadChatRooms: () => Promise<void>;
  loadOlderMessages: () => Promise<void>;
  hasMoreMessages: boolean;
  loadingOlderMessages: boolean;
}

const loadableMessages: Loadable<ChatMessage> = { loading: true };
const loadableChatRooms: Loadable<ChatRoom> = { loading: true };

const initialContext: ChatContextType = {
  messages: loadableMessages,
  chatRooms: loadableChatRooms,
  sendMessage: () => Promise.resolve(),
  joinRoom: () => {},
  createRoom: () => Promise.resolve({} as ChatRoom),
  updateRoom: () => Promise.resolve({} as ChatRoom),
  deleteRoom: () => Promise.resolve(),
  reloadChatRooms: () => Promise.resolve(),
  loadOlderMessages: () => Promise.resolve(),
  hasMoreMessages: false,
  loadingOlderMessages: false,
};

const ChatContext = createContext<ChatContextType>(initialContext);

interface ChatProviderProps {
  device?: Device;
  children: React.ReactNode;
}

const SOCKET_URL =
  getConfigVar('REACT_APP_SERVICE_URL') ||
  (window.location.protocol === 'https:' ? 'wss://' : 'ws://') + window.location.host;

function ChatProvider({ device, children }: ChatProviderProps) {
  const { auth } = useContext(AuthContext);
  const [messages, setMessages] = useState<Loadable<ChatMessage>>({ loading: true });
  const [chatRooms, setChatRooms] = useState<Loadable<ChatRoom>>({ loading: true });
  const [currentRoom, setCurrentRoom] = useState<ChatRoom | undefined>();
  const [hasMoreMessages, setHasMoreMessages] = useState<boolean>(false);
  const [loadingOlderMessages, setLoadingOlderMessages] = useState<boolean>(false);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [socket, setSocket] = useState<Socket | null>(null);
  const [socketConnected, setSocketConnected] = useState(false);
  const prevRoomId = useRef<string | undefined>(undefined);

  const reloadChatRooms = useCallback(async (): Promise<void> => {
    if (!device) return;
    setChatRooms({ loading: true });
    try {
      const response = await apiClient.get(`/api/cocs/${device.id}/rooms/member`);
      setChatRooms({ loading: false, data: response.data });
    } catch (err) {
      setChatRooms({ loading: false, error: err as Error });
    }
  }, [device]);

  useEffect(() => {
    reloadChatRooms();
  }, [reloadChatRooms]);

  const findLobbyRoom = (): ChatRoom | undefined => {
    if (!chatRooms?.data) return undefined;
    return chatRooms.data.find((room) => room.type === 'lobby');
  };

  const getEffectiveRoom = (): ChatRoom | undefined => {
    if (currentRoom) return currentRoom;
    return findLobbyRoom();
  };

  useEffect(() => {
    if (!device) return;

    // Load initial messages with pagination
    setMessages({ loading: true });
    setHasMoreMessages(false);
    setNextCursor(null);

    let endpoint;
    if (currentRoom && currentRoom.id) {
      endpoint = `/api/cocs/${device.id}/rooms/${currentRoom.id}/messages?limit=20`;
    } else {
      endpoint = `/api/cocs/${device.id}/lobby/messages?limit=20`;
    }

    apiClient
      .get(endpoint)
      .then((response) => {
        const data = response.data;
        const processedMessages = data.messages.map((m: ChatMessage) => ({
          ...m,
          createdAt: new Date(m.createdAt),
        }));

        setMessages({ loading: false, data: processedMessages });
        setHasMoreMessages(data.hasMore || false);
        setNextCursor(data.nextCursor || null);
      })
      .catch((err) => setMessages({ loading: false, error: err }));
  }, [device, currentRoom]);

  const sendMessage = (text: string) => {
    if (!device) return Promise.reject('no device');

    const handleError = (reject: Function, err: unknown) => {
      setMessages((prevState) => ({
        ...prevState,
        loading: false,
        error: err as Error,
      }));
      reject(err);
    };

    return new Promise<void>((resolve, reject) => {
      const effectiveRoom = getEffectiveRoom();
      const endpoint = effectiveRoom?.id
        ? `/api/cocs/${device.id}/rooms/${effectiveRoom.id}/messages`
        : `/api/cocs/${device.id}/lobby/messages`;

      apiClient
        .post<string, AxiosResponse<ChatMessage>>(endpoint, { text })
        .then(() => resolve())
        .catch((err) => handleError(reject, err));
    });
  };

  // Socket setup: connect when auth + device are available, listen for chat
  // events, and push incoming messages into the same `messages` state we use
  // for fetched history (dedupe inside the functional setter).
  useEffect(() => {
    if (!auth || !device) return;

    const socketInstance = io(SOCKET_URL, {
      path: '/socket.io',
      transports: ['websocket'],
      auth: {
        token: auth.token,
        cocId: device.id,
        roomId: currentRoom?.id || getEffectiveRoom()?.id,
      },
      withCredentials: true,
    });

    socketInstance.on('connect', () => setSocketConnected(true));
    socketInstance.on('disconnect', () => setSocketConnected(false));
    socketInstance.on('connect_error', (err) =>
      console.error('[socket] connect_error:', err),
    );
    socketInstance.on('chat:error', (error) => console.error('[socket] chat:error:', error));
    socketInstance.on('join:room:success', ({ roomId }) =>
      console.log('[socket] Join room success:', roomId),
    );
    socketInstance.on('leave:room:success', ({ roomId }) =>
      console.log('[socket] Leave room success:', roomId),
    );

    socketInstance.on('chat:new', (message: ChatMessage) => {
      const incoming = { ...message, createdAt: new Date(message.createdAt) };
      setMessages((prev) => {
        const existing = prev.data || [];
        if (existing.some((m) => m.id === incoming.id)) {
          console.log('[socket] Duplicate message ignored:', message.id);
          return prev;
        }
        return { ...prev, loading: false, data: [...existing, incoming] };
      });
    });

    // Room lifecycle: keep chatRooms in sync without a refetch. Server emits
    // chat:room-created/-updated/-deleted to everyone viewing the coc;
    // chat:room-added/-removed are targeted at the affected user.
    socketInstance.on('chat:room-created', (room: ChatRoom) => {
      setChatRooms((prev) => {
        const existing = prev.data || [];
        if (existing.some((r) => r.id === room.id)) return prev;
        return { ...prev, loading: false, data: [...existing, room] };
      });
    });

    socketInstance.on('chat:room-updated', (room: ChatRoom) => {
      setChatRooms((prev) => {
        if (!prev.data) return prev;
        return { ...prev, data: prev.data.map((r) => (r.id === room.id ? room : r)) };
      });
      setCurrentRoom((cur) => (cur?.id === room.id ? room : cur));
    });

    socketInstance.on('chat:room-deleted', ({ roomId }: { roomId: string }) => {
      setChatRooms((prev) => {
        if (!prev.data) return prev;
        return { ...prev, data: prev.data.filter((r) => r.id !== roomId) };
      });
      setCurrentRoom((cur) => (cur?.id === roomId ? undefined : cur));
    });

    socketInstance.on('chat:room-added', (room: ChatRoom) => {
      setChatRooms((prev) => {
        const existing = prev.data || [];
        if (existing.some((r) => r.id === room.id)) return prev;
        return { ...prev, loading: false, data: [...existing, room] };
      });
      if ('Notification' in window && Notification.permission === 'granted') {
        new Notification('Added to a chat room', {
          body: `You were added to "${room.name}"`,
          tag: `room-added-${room.id}`,
        });
      }
    });

    socketInstance.on('chat:room-removed', ({ roomId }: { roomId: string }) => {
      setChatRooms((prev) => {
        if (!prev.data) return prev;
        return { ...prev, data: prev.data.filter((r) => r.id !== roomId) };
      });
      setCurrentRoom((cur) => (cur?.id === roomId ? undefined : cur));
    });

    setSocket(socketInstance);

    return () => {
      socketInstance.disconnect();
      setSocket(null);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- room ids read via closure
  }, [auth, device]);

  // Socket room tracking: emit leave/join when the effective room changes.
  useEffect(() => {
    if (!socket || !socketConnected) return;

    const prev = prevRoomId.current;
    const nextRoomId = currentRoom?.id || getEffectiveRoom()?.id;
    if (prev && prev !== nextRoomId) {
      console.log('[socket] Leaving room:', prev);
      socket.emit('leave:room', { roomId: prev });
    }
    if (nextRoomId) {
      console.log('[socket] Joining room:', nextRoomId);
      socket.emit('join:room', { roomId: nextRoomId });
      prevRoomId.current = nextRoomId;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- getEffectiveRoom is local
  }, [socket, socketConnected, currentRoom]);

  const loadOlderMessages = () => {
    if (!device || !nextCursor || loadingOlderMessages) {
      return Promise.resolve();
    }

    setLoadingOlderMessages(true);

    let endpoint;
    if (currentRoom && currentRoom.id) {
      endpoint = `/api/cocs/${device.id}/rooms/${currentRoom.id}/messages?limit=20&before=${nextCursor}`;
    } else {
      endpoint = `/api/cocs/${device.id}/lobby/messages?limit=20&before=${nextCursor}`;
    }

    return apiClient
      .get(endpoint)
      .then((response) => {
        const data = response.data;
        const processedMessages = data.messages.map((m: ChatMessage) => ({
          ...m,
          createdAt: new Date(m.createdAt),
        }));

        setMessages((prev) => ({
          ...prev,
          data: [...processedMessages, ...(prev.data || [])],
        }));

        setHasMoreMessages(data.hasMore || false);
        setNextCursor(data.nextCursor || null);
      })
      .catch((err) => {
        console.error('Failed to load older messages:', err);
        throw err;
      })
      .finally(() => {
        setLoadingOlderMessages(false);
      });
  };

  const joinRoom = (room: ChatRoom | undefined) => {
    setCurrentRoom(room);
  };

  const createRoom = (name: string, type?: string, members?: string[]) => {
    if (!device) return Promise.reject('no device');

    return new Promise<ChatRoom>((resolve, reject) => {
      const endpoint = `/api/cocs/${device.id}/rooms`;
      const data = {
        name,
        ...(type && { type }),
        ...(members && { members }),
      };

      apiClient
        .post<any, AxiosResponse<ChatRoom>>(endpoint, data)
        .then((response) => {
          // Add the new room to the chatRooms state
          if (chatRooms?.data) {
            const updatedRooms = [...chatRooms.data, response.data];
            setChatRooms({ loading: false, data: updatedRooms });
          }

          // Switch the user into the room they just created. The room-tracking
          // effect picks up the currentRoom change and emits join:room on the
          // socket; the message-loading effect refetches messages for the new
          // room.
          setCurrentRoom(response.data);

          resolve(response.data);
        })
        .catch((err) => {
          reject(err);
        });
    });
  };

  const updateRoom = (room: ChatRoom, name: string, members?: string[]) => {
    if (!device) return Promise.reject('no device');
    if (!room || !room.id) return Promise.reject('invalid room');

    return new Promise<ChatRoom>((resolve, reject) => {
      const endpoint = `/api/cocs/${device.id}/rooms/${room.id}`;
      const data = { name, ...(members && { members }) };

      apiClient
        .put<any, AxiosResponse<ChatRoom>>(endpoint, data)
        .then((response) => {
          if (chatRooms?.data) {
            const updatedRooms = chatRooms.data.map((r) =>
              r.id === response.data.id ? response.data : r,
            );
            setChatRooms({ loading: false, data: updatedRooms });
          }

          // Update currentRoom if it's the one being edited
          if (currentRoom && currentRoom.id === response.data.id) {
            setCurrentRoom(response.data);
          }

          resolve(response.data);
        })
        .catch((err) => {
          reject(err);
        });
    });
  };

  const deleteRoom = (room: ChatRoom) => {
    if (!device) return Promise.reject('no device');
    if (!room || !room.id) return Promise.reject('invalid room');

    return new Promise<void>((resolve, reject) => {
      const endpoint = `/api/cocs/${device.id}/rooms/${room.id}`;

      apiClient
        .delete(endpoint)
        .then(() => {
          if (chatRooms?.data) {
            const updatedRooms = chatRooms.data.filter((r) => r.id !== room.id);
            setChatRooms({ loading: false, data: updatedRooms });
          }

          if (currentRoom && currentRoom.id === room.id) {
            setCurrentRoom(undefined);
          }

          resolve();
        })
        .catch((err) => {
          reject(err);
        });
    });
  };

  const effectiveRoom = getEffectiveRoom();
  return (
    <ChatContext.Provider
      value={{
        device,
        messages,
        chatRooms,
        currentRoom,
        effectiveRoom,
        sendMessage,
        joinRoom,
        createRoom,
        updateRoom,
        deleteRoom,
        reloadChatRooms,
        loadOlderMessages,
        hasMoreMessages,
        loadingOlderMessages,
      }}
    >
      {children}
    </ChatContext.Provider>
  );
}

export { ChatContext, ChatProvider };
